import { useState } from 'react'
import {
  Hexagon,
  Bell,
  Search,
  PanelRightOpen,
  Zap,
  Shield,
} from 'lucide-react'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

interface TopCommandBandProps {
  activeSurface: string
  onToggleIntelligence: () => void
}

const SURFACE_TITLES: Record<string, string> = {
  portfolio: 'Portfolio Proof System',
  'writing-stone': 'Writing Stone / CanonWeaver',
  jobs: 'Job Acquisition Cockpit',
  evidence: 'Evidence Recovery Center',
  registry: 'Ark Registry',
  notebooklm: 'NotebookLM Audit Station',
}

export function TopCommandBand({ activeSurface, onToggleIntelligence }: TopCommandBandProps) {
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <header className="h-12 bg-ark-deep/80 backdrop-blur-md border-b border-ark-panel/50 flex items-center px-4 gap-4 z-ark-topBand shrink-0">
      {/* Logo / Brand */}
      <div className="flex items-center gap-2.5 shrink-0">
        <div className="relative">
          <Hexagon className="w-6 h-6 text-ark-gold" strokeWidth={1.5} />
          <Zap className="w-3 h-3 text-ark-gold absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-ark-text-bright leading-none tracking-tight">
            ARK
          </span>
          <span className="text-[0.6rem] font-mono text-ark-text-muted uppercase tracking-wider leading-none">
            Design Swarm
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-ark-panel/50" />

      {/* Surface Title */}
      <div className="flex items-center gap-2 flex-1">
        <Shield className="w-3.5 h-3.5 text-ark-gold-dim" />
        <h1 className="text-sm font-medium text-ark-text-dim">
          {SURFACE_TITLES[activeSurface] || 'Ark Command Center'}
        </h1>
      </div>

      {/* Command Zone */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className={cn('flex items-center transition-all duration-300', searchOpen ? 'w-64' : 'w-8')}>
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="p-1.5 rounded-ark-sm hover:bg-ark-panel/50 text-ark-text-dim hover:text-ark-text transition-colors shrink-0"
          >
            <Search className="w-4 h-4" />
          </button>
          {searchOpen && (
            <input
              type="text"
              placeholder="Search across all surfaces..."
              className="ml-2 bg-ark-panel/50 border border-ark-panel/50 rounded-ark-sm px-2 py-1 text-xs text-ark-text placeholder:text-ark-text-muted focus:outline-none focus:border-ark-gold/30 w-full"
              autoFocus
              onBlur={() => setSearchOpen(false)}
            />
          )}
        </div>

        {/* Notifications */}
        <button className="p-1.5 rounded-ark-sm hover:bg-ark-panel/50 text-ark-text-dim hover:text-ark-text transition-colors relative">
          <Bell className="w-4 h-4" />
          <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-ark-danger rounded-full" />
        </button>

        {/* Intelligence Toggle */}
        <button
          onClick={onToggleIntelligence}
          className="p-1.5 rounded-ark-sm hover:bg-ark-panel/50 text-ark-text-dim hover:text-ark-text transition-colors"
          title="Toggle Intelligence Rail"
        >
          <PanelRightOpen className="w-4 h-4" />
        </button>
      </div>
    </header>
  )
}
